import {
  gql,
  type ApolloQueryResult,
} from '@apollo/client';

import { apolloClient } from './client';

interface GraphQLErrorPayload {
  message?: string;
  extensions?: {
    code?: string;
    statusCode?: number;
    errorCode?: string;
    retryAfterSeconds?: number;
    originalError?: {
      message?: string | string[];
      statusCode?: number;
      errorCode?: string;
    };
  };
}

interface ExecuteGraphqlRequestOptions {
  accessToken?: string;
  requestName: string;
}

interface GraphQLErrorDetails {
  code: string | null;
  message: string | null;
  statusCode: number | null;
}

interface GraphqlRequestErrorOptions {
  code?: string;
  statusCode?: number;
  retryAfterSeconds?: number;
}

export class GraphqlRequestError extends Error {
  readonly code?: string;
  readonly statusCode?: number;
  // Seconds to wait before retrying, when the API refused for too many attempts.
  readonly retryAfterSeconds?: number;

  constructor(message: string, options?: GraphqlRequestErrorOptions) {
    super(message);
    this.name = 'GraphqlRequestError';
    this.code = options?.code;
    this.statusCode = options?.statusCode;
    this.retryAfterSeconds = options?.retryAfterSeconds;
    Object.setPrototypeOf(this, GraphqlRequestError.prototype);
  }
}

function normalizeErrorCode(errorCode: unknown): string | null {
  if (typeof errorCode !== 'string') {
    return null;
  }

  const normalizedErrorCode = errorCode.trim();
  return normalizedErrorCode || null;
}

function normalizeStatusCode(statusCode: unknown): number | null {
  if (typeof statusCode !== 'number' || !Number.isFinite(statusCode)) {
    return null;
  }

  const normalizedStatusCode = Math.trunc(statusCode);
  if (normalizedStatusCode < 100 || normalizedStatusCode > 599) {
    return null;
  }

  return normalizedStatusCode;
}

function extractGraphQLErrorDetails(errorItem: GraphQLErrorPayload): GraphQLErrorDetails {
  const messages = errorItem.extensions?.originalError?.message;
  const detailedMessage = Array.isArray(messages)
    ? messages.map((message) => message.trim()).filter(Boolean).join(' ')
    : typeof messages === 'string'
      ? messages.trim()
      : '';

  const fallbackMessage = errorItem.message?.trim() || null;
  const code =
    normalizeErrorCode(errorItem.extensions?.originalError?.errorCode) ??
    normalizeErrorCode(errorItem.extensions?.errorCode) ??
    normalizeErrorCode(errorItem.extensions?.code);
  const statusCode =
    normalizeStatusCode(errorItem.extensions?.originalError?.statusCode) ??
    normalizeStatusCode(errorItem.extensions?.statusCode);

  return {
    message: detailedMessage || fallbackMessage,
    code,
    statusCode,
  };
}

function normalizeNetworkErrorMessage(errorMessage: string): string {
  const normalizedMessage = errorMessage.trim();
  if (!normalizedMessage) {
    return normalizedMessage;
  }

  const normalizedMessageLowercase = normalizedMessage.toLowerCase();
  if (
    normalizedMessageLowercase.includes('failed to fetch') ||
    normalizedMessageLowercase.includes('network request failed')
  ) {
    return 'Unable to reach the API. Ensure the API server is running.';
  }

  return normalizedMessage;
}

interface ApolloErrorLike {
  errors?: GraphQLErrorPayload[];
  graphQLErrors?: GraphQLErrorPayload[];
  networkError?: {
    message?: string;
    result?: {
      errors?: GraphQLErrorPayload[];
    };
  };
  message?: string;
}

function isApolloErrorLike(error: unknown): error is ApolloErrorLike {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const typedError = error as ApolloErrorLike;
  return (
    Array.isArray(typedError.graphQLErrors) ||
    Array.isArray(typedError.errors) ||
    Boolean(typedError.networkError)
  );
}

function combineGraphQLErrorDetails(errors: GraphQLErrorPayload[]): GraphQLErrorDetails {
  let message: string | null = null;
  let code: string | null = null;
  let statusCode: number | null = null;

  for (const errorItem of errors) {
    const errorDetails = extractGraphQLErrorDetails(errorItem);
    if (!message && errorDetails.message) {
      message = errorDetails.message;
    }
    if (!code && errorDetails.code) {
      code = errorDetails.code;
    }
    if (!statusCode && errorDetails.statusCode) {
      statusCode = errorDetails.statusCode;
    }

    if (message && code && statusCode) {
      break;
    }
  }

  return {
    message,
    code,
    statusCode,
  };
}

function extractApolloErrorDetails(error: ApolloErrorLike): GraphQLErrorDetails {
  const graphQLErrorDetails = combineGraphQLErrorDetails([
    ...(error.graphQLErrors ?? []),
    ...(error.errors ?? []),
  ]);
  if (graphQLErrorDetails.message) {
    return graphQLErrorDetails;
  }

  const networkGraphQLErrorDetails = combineGraphQLErrorDetails(
    error.networkError?.result?.errors ?? []
  );
  if (networkGraphQLErrorDetails.message) {
    return {
      message: networkGraphQLErrorDetails.message,
      code: graphQLErrorDetails.code ?? networkGraphQLErrorDetails.code,
      statusCode: graphQLErrorDetails.statusCode ?? networkGraphQLErrorDetails.statusCode,
    };
  }

  const networkErrorMessage = error.networkError?.message;
  if (typeof networkErrorMessage === 'string') {
    const normalizedNetworkErrorMessage = normalizeNetworkErrorMessage(networkErrorMessage);
    if (normalizedNetworkErrorMessage) {
      return {
        message: normalizedNetworkErrorMessage,
        code: graphQLErrorDetails.code ?? networkGraphQLErrorDetails.code,
        statusCode: graphQLErrorDetails.statusCode ?? networkGraphQLErrorDetails.statusCode,
      };
    }
  }

  const apolloErrorMessage = error.message?.trim();
  return {
    message: apolloErrorMessage || null,
    code: graphQLErrorDetails.code ?? networkGraphQLErrorDetails.code,
    statusCode: graphQLErrorDetails.statusCode ?? networkGraphQLErrorDetails.statusCode,
  };
}

function resolveRequestErrorDetails(error: unknown): GraphQLErrorDetails {
  if (isApolloErrorLike(error)) {
    return extractApolloErrorDetails(error);
  }

  if (error instanceof Error) {
    const normalizedMessage = normalizeNetworkErrorMessage(error.message);
    return {
      message: normalizedMessage || null,
      code: null,
      statusCode: null,
    };
  }

  return {
    message: null,
    code: null,
    statusCode: null,
  };
}

function findRetryAfterSeconds(error: unknown): number | undefined {
  if (!isApolloErrorLike(error)) {
    return undefined;
  }

  const errors = [
    ...(error.graphQLErrors ?? []),
    ...(error.errors ?? []),
    ...(error.networkError?.result?.errors ?? []),
  ];
  const retryAfterSeconds = errors
    .map((errorItem) => errorItem.extensions?.retryAfterSeconds)
    .find((value) => typeof value === 'number' && Number.isFinite(value) && value > 0);

  return retryAfterSeconds;
}

function buildRequestContext(accessToken?: string) {
  if (!accessToken) {
    return undefined;
  }

  return {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };
}

async function executeCacheAndNetworkQuery<TData>(
  queryDocument: ReturnType<typeof gql>,
  variables: Record<string, unknown>,
  context: ReturnType<typeof buildRequestContext>
): Promise<ApolloQueryResult<TData>> {
  const observableQuery = apolloClient.watchQuery<TData>({
    query: queryDocument,
    variables,
    context,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  try {
    return await new Promise<ApolloQueryResult<TData>>((resolve, reject) => {
      let subscription: { unsubscribe: () => void } | null = null;

      const disposeSubscription = () => {
        if (subscription) {
          subscription.unsubscribe();
          subscription = null;
        }
      };

      subscription = observableQuery.subscribe({
        next: (result) => {
          // Apollo Client 4 reports a failed query on the result instead of erroring the stream.
          if (result.error) {
            disposeSubscription();
            reject(result.error);
            return;
          }

          if (!result.loading) {
            disposeSubscription();
            resolve(result);
          }
        },
        error: (error) => {
          disposeSubscription();
          reject(error);
        },
      });
    });
  } finally {
    observableQuery.stop();
  }
}

export function readCachedGraphqlQuery<TData>(
  query: string | ReturnType<typeof gql>,
  variables: Record<string, unknown> = {}
): TData | undefined {
  const queryDocument = typeof query === 'string' ? gql(query) : query;

  try {
    const cachedData = apolloClient.readQuery<TData>({
      query: queryDocument,
      variables,
    });

    return cachedData === null ? undefined : cachedData;
  } catch {
    return undefined;
  }
}

export async function executeGraphqlRequest<TData>(
  query: string,
  variables: Record<string, unknown>,
  options: ExecuteGraphqlRequestOptions
): Promise<TData> {
  const normalizedQuery = query.trimStart();
  const isMutation = normalizedQuery.startsWith('mutation');
  const context = buildRequestContext(options.accessToken);
  const queryDocument = gql(query);

  try {
    const response = isMutation
      ? await apolloClient.mutate<TData>({
          mutation: queryDocument,
          variables,
          context,
        })
      : await executeCacheAndNetworkQuery<TData>(queryDocument, variables, context);

    if (!response.data) {
      throw new Error(`${options.requestName} response did not include data.`);
    }

    return response.data as TData;
  } catch (error) {
    const requestErrorDetails = resolveRequestErrorDetails(error);
    if (requestErrorDetails.message) {
      throw new GraphqlRequestError(requestErrorDetails.message, {
        code: requestErrorDetails.code ?? undefined,
        statusCode: requestErrorDetails.statusCode ?? undefined,
        retryAfterSeconds: findRetryAfterSeconds(error),
      });
    }

    throw new GraphqlRequestError(`${options.requestName} request failed.`);
  }
}
